import React, { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, getDay, isPast, isToday, parseISO, isBefore, isAfter, startOfDay } from 'date-fns';
import { cn } from '../lib/utils';
import * as Lu from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function ScheduleCalendar({ enrollment, attendance = [], onAction }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const scheduledDays = enrollment.scheduled_days || [];

  const enrollStart = enrollment.enrollment_start_date ? startOfDay(parseISO(enrollment.enrollment_start_date)) : null;
  const enrollEnd = enrollment.enrollment_end_date ? startOfDay(parseISO(enrollment.enrollment_end_date)) : null;

  const monthData = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    return {
      name: format(monthStart, 'MMMM yyyy'),
      days: eachDayOfInterval({ start: monthStart, end: monthEnd })
    };
  }, [currentMonth]);

  const isInEnrollmentRange = (date) => {
    const d = startOfDay(date);
    if (enrollStart && isBefore(d, enrollStart)) return false;
    if (enrollEnd && isAfter(d, enrollEnd)) return false;
    return true;
  };

  const getDayStatus = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayName = format(date, 'EEE');
    const isProjected = scheduledDays.includes(dayName);
    const inRange = isInEnrollmentRange(date);
    const records = attendance.filter(a => isSameDay(new Date(a.timestamp), date));
    const isMakeupForThis = attendance.find(a => a.makeup_for_date === dateStr);
    return {
      isProjected,
      records,
      isMakeupForThis,
      wasInPast: isPast(date) && !isToday(date),
      inRange
    };
  };

  const actionableDays = useMemo(() => {
    return monthData.days.filter(date => {
      const { isProjected, records, wasInPast, isMakeupForThis, inRange } = getDayStatus(date);
      if (!inRange) return false;
      if (records.length > 0) return false;
      if (isProjected && wasInPast && !isMakeupForThis) return true;
      return false;
    }).sort((a, b) => b - a);
  }, [monthData.days, attendance, scheduledDays, enrollStart, enrollEnd]);

  const renderDay = (date) => {
    const { isProjected, records, isMakeupForThis, inRange } = getDayStatus(date);

    const hasAttendance = records.some(r => r.status === 'present');
    const isManuallyAbsent = records.some(r => r.attendance_type === 'absent' || r.attendance_type === 'no_class');
    // Extra: attendance_type === 'extra'
    const isExtra = records.some(r => r.attendance_type === 'extra');
    // Makeup: attendance_type === 'makeup'
    const isMakeup = records.some(r => r.attendance_type === 'makeup');
    const hasAnyRecord = records.length > 0;
    const isClickable = inRange;

    return (
      <div
        key={date.toString()}
        onClick={() => isClickable ? onAction(date, getDayStatus(date)) : null}
        className={cn(
          "relative h-12 w-12 flex items-center justify-center rounded-2xl transition-all border border-transparent group",
          isClickable ? "cursor-pointer hover:bg-slate-50" : "cursor-default opacity-35",
          isToday(date) && inRange ? "bg-primary-50 text-primary-700 border-primary-200" : ""
        )}
      >
        <div className={cn(
          "relative w-9 h-9 flex items-center justify-center text-xs font-black rounded-full transition-transform",
          isClickable && "group-hover:scale-110",
          isProjected && !isMakeupForThis && !hasAnyRecord && inRange && "border-2 border-primary-500 text-primary-700",
          !isProjected && hasAnyRecord && "border border-slate-200 dark:border-slate-700 rounded-lg",
          isMakeupForThis && "line-through text-slate-300 decoration-rose-500 decoration-2",
          !isProjected && !hasAnyRecord && "text-slate-400",
        )}>
          {format(date, 'd')}

          {hasAnyRecord || isMakeupForThis ? (
            <div className="absolute inset-0 pointer-events-none">
              {/* Regular present (not extra, not makeup) */}
              {hasAttendance && !isExtra && !isMakeup && (
                <div className="absolute top-0.5 right-0.5 bg-emerald-500 text-white rounded-full p-0.5 shadow-sm border border-white">
                  <SafeIcon icon={Lu.LuCheck} size={6} />
                </div>
              )}
              {/* Absent / No Class */}
              {isManuallyAbsent && !hasAttendance && !isExtra && !isMakeup && (
                <div className="absolute top-0.5 right-0.5 bg-rose-500 text-white rounded-full p-0.5 shadow-sm border border-white">
                  <SafeIcon icon={Lu.LuX} size={6} />
                </div>
              )}
              {/* Extra lesson — amber star */}
              {isExtra && (
                <div className="absolute top-0.5 right-0.5 bg-amber-500 text-white rounded-full p-0.5 shadow-sm border border-white">
                  <SafeIcon icon={Lu.LuStar} size={6} className="fill-white" />
                </div>
              )}
              {/* Makeup class — tick in light blue circle */}
              {isMakeup && (
                <div className="absolute bottom-0.5 right-0.5 bg-sky-400 text-white rounded-full p-0.5 shadow-sm border border-white">
                  <SafeIcon icon={Lu.LuCheck} size={6} />
                </div>
              )}
              {/* Makeup target (original missed date) */}
              {isMakeupForThis && !isMakeup && (
                <div className="absolute bottom-0.5 left-0.5 bg-indigo-500 text-white rounded-full p-0.5 shadow-sm border border-white">
                  <SafeIcon icon={Lu.LuRefreshCw} size={6} />
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
      {/* Calendar Header */}
      <div className="p-6 border-b flex justify-between items-center bg-slate-50/50">
        <h4 className="text-sm font-bold text-slate-500">{monthData.name}</h4>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            className="p-2 hover:bg-white rounded-xl border border-transparent hover:border-slate-200 transition-all text-slate-400 hover:text-primary-600"
          >
            <SafeIcon icon={Lu.LuChevronLeft} size={20} />
          </button>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            className="p-2 hover:bg-white rounded-xl border border-transparent hover:border-slate-200 transition-all text-slate-400 hover:text-primary-600"
          >
            <SafeIcon icon={Lu.LuChevronRight} size={20} />
          </button>
        </div>
      </div>

      {/* Enrollment Period Banner */}
      <div className="px-6 py-3 bg-indigo-50 dark:bg-indigo-950/30 border-b border-indigo-100 flex items-center gap-2">
        <SafeIcon icon={Lu.LuCalendarRange} size={13} className="text-indigo-500 shrink-0" />
        <p className="text-[9px] font-bold text-indigo-600">
          {enrollStart
            ? `Enrolled from ${format(enrollStart, 'dd MMM yyyy')}${enrollEnd ? ` → ${format(enrollEnd, 'dd MMM yyyy')}` : ' (ongoing)'}`
            : enrollEnd
              ? `Ends ${format(enrollEnd, 'dd MMM yyyy')}`
              : 'No enrollment dates set — all dates active'}
        </p>
      </div>

      {/* Calendar Body */}
      <div className="p-6">
        <div className="grid grid-cols-7 gap-2 mb-4">
          {DAYS.map(d => (
            <div key={d} className="text-[9px] font-black text-center text-slate-300 uppercase tracking-tighter">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {Array(getDay(monthData.days[0])).fill(0).map((_, i) => <div key={`empty-${i}`} />)}
          {monthData.days.map(renderDay)}
        </div>
      </div>

      {/* Action Required */}
      {actionableDays.length > 0 && (
        <div className="px-6 py-4 bg-slate-50/30 border-t space-y-3">
          <p className="text-[10px] font-bold text-slate-400 px-1">Action Required</p>
          <div className="space-y-2">
            {actionableDays.map(date => (
              <div
                key={date.toString()}
                className="flex justify-between items-center bg-white dark:bg-slate-800 p-3 rounded-2xl border border-slate-100 shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 border border-rose-100 flex items-center justify-center font-black text-xs">
                    {format(date, 'd')}
                  </div>
                  <div>
                    <p className="text-[10px] font-bold dark:text-white">{format(date, 'eee, dd MMM')}</p>
                    <p className="text-[8px] font-medium text-slate-400">Missed Class</p>
                  </div>
                </div>
                <button
                  onClick={() => onAction(date, getDayStatus(date))}
                  className="px-3 py-1.5 bg-slate-900 text-white text-[9px] font-bold rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Select
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="px-6 py-4 bg-slate-50/50 border-t flex flex-wrap gap-4 text-[9px] font-medium text-slate-400">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 border-2 border-primary-500 rounded-full" />
          Projected
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-emerald-500 rounded-full flex items-center justify-center text-white">
            <SafeIcon icon={Lu.LuCheck} size={6} />
          </div>
          Attended
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-rose-500 rounded-full flex items-center justify-center text-white">
            <SafeIcon icon={Lu.LuX} size={6} />
          </div>
          Absent
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-amber-500 rounded-full flex items-center justify-center text-white">
            <SafeIcon icon={Lu.LuStar} size={6} className="fill-white" />
          </div>
          Extra
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-sky-400 rounded-full flex items-center justify-center text-white">
            <SafeIcon icon={Lu.LuCheck} size={6} />
          </div>
          Makeup
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-slate-200 rounded-full opacity-40" />
          Outside Enrollment
        </div>
      </div>
    </div>
  );
}